import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../components/layout/PageHeader';
import ServiceComposeEntry from '../components/services/ServiceComposeEntry';
import { AssistantConversation } from '../components/layout/AssistantSidebar';
import { SpecPreview } from '../components/composer/ComposerMessage';
import { AssistantProvider, useAssistant, useOptionalAssistant } from '../providers/AssistantProvider';
import { useFlag } from '../hooks/useFeatureFlags';
import { useRequiresPlan } from '../hooks/useRequiresPlan';

/**
 * Where the page is, carried in the history entry rather than component state
 * (task 1117), so a browser refresh mid-conversation comes back to the
 * conversation — which the provider restores from the thread — instead of the
 * empty entry panel. `prompt` is the hand-off from the AI Services first-run
 * panel and the Dashboard onboarding; it is consumed once and replaced.
 */
interface ComposeLocationState {
  prompt?: string;
  conversing?: boolean;
}

/**
 * Compose-based service creation at /services/new (task 392, Mockup G).
 *
 * Two states: before a description is submitted, the Mockup G chat-create
 * panel is the page; once submitted (or when arriving with a prompt in
 * location.state), the page is the Assistant conversation, full width — the
 * `page` presentation of `AssistantProvider` (task 1117). The description is
 * the first turn of a new general thread on `/mw/assistant`, so the
 * conversation is kept, listed in history, and filed under the service it
 * builds; the docked sidebar shows only its rail on this route.
 */
export default function ServiceComposePage() {
  // The page needs the conversation even when the sidebar is rolled back
  // (`innovate.fe.assistant.sidebar` off mounts no provider in AppLayout), so
  // it brings its own in that case rather than losing the build surface.
  const assistant = useOptionalAssistant();
  if (!assistant) {
    return (
      <AssistantProvider>
        <ServiceComposeBody />
      </AssistantProvider>
    );
  }
  return <ServiceComposeBody />;
}

function ServiceComposeBody() {
  const location = useLocation();
  const navigate = useNavigate();
  const { seedFirstTurn, turns } = useAssistant();
  const requiresPlan = useRequiresPlan();
  const specCardEnabled = useFlag('innovate.fe.assistant.specCard');

  const state = (location.state as ComposeLocationState | null) ?? {};
  const handOffPrompt = state.prompt?.trim() || null;
  const conversing = Boolean(state.conversing) || (handOffPrompt !== null && !requiresPlan);

  const start = (description: string) => {
    void seedFirstTurn(description);
    navigate(location.pathname, { replace: true, state: { conversing: true } });
  };

  // The hand-off prompt is sent exactly once, on mount. The ref guards
  // StrictMode's double effect; `!requiresPlan` keeps a seeded prompt from
  // being spent on a guaranteed failure (the task-467 rule the entry keeps).
  const handOffSentRef = useRef(false);
  useEffect(() => {
    if (!handOffPrompt || handOffSentRef.current || requiresPlan) return;
    handOffSentRef.current = true;
    start(handOffPrompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * The optional spec column (task 1117): the plan the latest design turn
   * proposed, beside the conversation, only where the spec card is on — the
   * same flag the conversation's inline card reads, so the page and the sidebar
   * make the plan display decision once. Gone once that plan is built.
   */
  const latestDesign = [...turns]
    .reverse()
    .find((turn) => turn.role === 'assistant' && (turn.spec || turn.buildResult));
  const columnSpec =
    specCardEnabled && latestDesign?.spec && !latestDesign.buildResult ? latestDesign.spec : null;

  return (
    <div>
      <PageHeader
        title="New AI Service"
        description="Describe what it should do — a chat guide designs it with you and builds it live."
      />

      <div className="mt-6 max-w-screen-2xl mx-auto">
        {!conversing ? (
          <div className="max-w-2xl mx-auto">
            <ServiceComposeEntry onSubmit={start} />
          </div>
        ) : (
          <div
            data-testid="service-compose-conversation"
            className={
              columnSpec
                ? 'lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-6'
                : undefined
            }
          >
            {/* The conversation's flex rules (task 766) need a bounded column:
                the stream scrolls inside it and the input rests on its floor. */}
            <div className="flex h-[calc(100vh-220px)] min-h-[480px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
              <AssistantConversation />
            </div>
            {columnSpec && (
              <div data-testid="service-compose-spec-column" className="mt-6 lg:mt-0">
                <div className="composer-scroll max-h-[calc(100vh-220px)] overflow-y-auto">
                  <SpecPreview spec={columnSpec} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
